// Billing Service - API endpoints for quotes and invoices management

import { api } from './api';
import type {
  ServiceQuote,
  ServiceQuoteItem,
  ServiceInvoice,
  QuoteListResponse,
  QuoteFilters,
  QuoteCreateRequest,
  QuoteUpdateRequest,
  QuoteItemCreateRequest,
  QuoteItemUpdateRequest,
  InvoiceListResponse,
  InvoiceFilters,
  InvoiceCreateRequest,
  RecordPaymentRequest,
  LinkInvoiceRequest,
  RejectQuoteRequest,
  ServiceInvoicePayment,
} from '@/types/billing';

export const billingService = {
  // ─── Quotes ───────────────────────────────────────────────

  /**
   * List quotes with filters and pagination
   * GET /billing/quotes
   */
  async getQuotes(filters?: QuoteFilters): Promise<QuoteListResponse> {
    const params: Record<string, string | number> = {};
    if (filters?.skip != null) params.skip = filters.skip;
    if (filters?.limit != null) params.limit = filters.limit;
    if (filters?.status && filters.status !== 'all') params.status = filters.status;
    if (filters?.search?.trim()) params.search = filters.search.trim();
    if (filters?.contact_id) params.contact_id = filters.contact_id;
    if (filters?.assigned_to_id) params.assigned_to_id = filters.assigned_to_id;
    if (filters?.from_date) params.from_date = filters.from_date;
    if (filters?.to_date) params.to_date = filters.to_date;

    const { data } = await api.get<QuoteListResponse>('/billing/quotes/', { params });
    return data;
  },

  /**
   * Get quote by ID (includes items)
   * GET /billing/quotes/{id}
   */
  async getQuote(id: string): Promise<ServiceQuote> {
    const { data } = await api.get<ServiceQuote>(`/billing/quotes/${id}`);
    return data;
  },

  /**
   * Create a new quote
   * POST /billing/quotes
   */
  async createQuote(request: QuoteCreateRequest): Promise<ServiceQuote> {
    const { data } = await api.post<ServiceQuote>('/billing/quotes/', request);
    return data;
  },

  /**
   * Update quote
   * PATCH /billing/quotes/{id}
   */
  async updateQuote(id: string, request: QuoteUpdateRequest): Promise<ServiceQuote> {
    const { data } = await api.patch<ServiceQuote>(`/billing/quotes/${id}`, request);
    return data;
  },

  /**
   * Delete quote (only drafts)
   * DELETE /billing/quotes/{id}
   */
  async deleteQuote(id: string): Promise<void> {
    await api.delete(`/billing/quotes/${id}`);
  },

  // ─── Quote Items ──────────────────────────────────────────

  /**
   * Add item to quote
   * POST /billing/quotes/{id}/items
   */
  async addQuoteItem(quoteId: string, request: QuoteItemCreateRequest): Promise<ServiceQuoteItem> {
    const { data } = await api.post<ServiceQuoteItem>(
      `/billing/quotes/${quoteId}/items`,
      request,
    );
    return data;
  },

  /**
   * Update quote item
   * PATCH /billing/quotes/{quoteId}/items/{itemId}
   */
  async updateQuoteItem(
    quoteId: string,
    itemId: string,
    request: QuoteItemUpdateRequest,
  ): Promise<ServiceQuoteItem> {
    const { data } = await api.patch<ServiceQuoteItem>(
      `/billing/quotes/${quoteId}/items/${itemId}`,
      request,
    );
    return data;
  },

  /**
   * Delete quote item
   * DELETE /billing/quotes/{quoteId}/items/{itemId}
   */
  async deleteQuoteItem(quoteId: string, itemId: string): Promise<void> {
    await api.delete(`/billing/quotes/${quoteId}/items/${itemId}`);
  },

  // ─── Quote Actions ────────────────────────────────────────

  /**
   * Send quote to client (generates public token, sends email)
   * POST /billing/quotes/{id}/send
   */
  async sendQuote(id: string): Promise<ServiceQuote> {
    const { data } = await api.post<ServiceQuote>(`/billing/quotes/${id}/send`, {});
    return data;
  },

  /**
   * Accept quote (admin action)
   * POST /billing/quotes/{id}/accept
   */
  async acceptQuote(id: string): Promise<ServiceQuote> {
    const { data } = await api.post<ServiceQuote>(`/billing/quotes/${id}/accept`, {});
    return data;
  },

  /**
   * Reject quote (admin action)
   * POST /billing/quotes/{id}/reject
   */
  async rejectQuote(id: string, reason?: string): Promise<ServiceQuote> {
    const body: RejectQuoteRequest = {};
    if (reason) body.rejection_reason = reason;
    const { data } = await api.post<ServiceQuote>(`/billing/quotes/${id}/reject`, body);
    return data;
  },

  /**
   * Cancel quote
   * POST /billing/quotes/{id}/cancel
   */
  async cancelQuote(id: string): Promise<ServiceQuote> {
    const { data } = await api.post<ServiceQuote>(`/billing/quotes/${id}/cancel`, {});
    return data;
  },

  /**
   * Create revision of a quote (new version)
   * POST /billing/quotes/{id}/revision
   */
  async createRevision(id: string): Promise<ServiceQuote> {
    const { data } = await api.post<ServiceQuote>(`/billing/quotes/${id}/revision`, {});
    return data;
  },

  /**
   * Convert quote to invoice
   * POST /billing/quotes/{id}/invoice
   */
  async invoiceQuote(id: string): Promise<ServiceInvoice> {
    const { data } = await api.post<ServiceInvoice>(`/billing/quotes/${id}/invoice`, {});
    return data;
  },

  /**
   * Download quote as PDF (authenticated)
   * GET /billing/quotes/{id}/pdf
   */
  async downloadQuotePdf(id: string): Promise<void> {
    const response = await api.get(`/billing/quotes/${id}/pdf`, {
      responseType: 'arraybuffer',
    });
    const blob = new Blob([response.data as ArrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const disposition = (response.headers as Record<string, string>)['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    link.download = match ? match[1] : `presupuesto_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  },

  // ─── Invoices ─────────────────────────────────────────────

  /**
   * List invoices with filters and pagination
   * GET /billing/invoices
   */
  async getInvoices(filters?: InvoiceFilters): Promise<InvoiceListResponse> {
    const params: Record<string, string | number> = {};
    if (filters?.skip != null) params.skip = filters.skip;
    if (filters?.limit != null) params.limit = filters.limit;
    if (filters?.status && filters.status !== 'all') params.status = filters.status;
    if (filters?.source && filters.source !== 'all') params.source = filters.source;
    if (filters?.search?.trim()) params.search = filters.search.trim();
    if (filters?.contact_id) params.contact_id = filters.contact_id;
    if (filters?.from_date) params.from_date = filters.from_date;
    if (filters?.to_date) params.to_date = filters.to_date;

    const { data } = await api.get<InvoiceListResponse>('/billing/invoices/', { params });
    return data;
  },

  /**
   * Create standalone invoice (without quote)
   * POST /billing/invoices/
   */
  async createInvoice(request: InvoiceCreateRequest): Promise<ServiceInvoice> {
    const { data } = await api.post<ServiceInvoice>('/billing/invoices/', request);
    return data;
  },

  /**
   * Get invoice by ID (includes line items and payments)
   * GET /billing/invoices/{id}
   */
  async getInvoice(id: string): Promise<ServiceInvoice> {
    const { data } = await api.get<ServiceInvoice>(`/billing/invoices/${id}`);
    return data;
  },

  /**
   * Record payment on invoice
   * POST /billing/invoices/{id}/payment
   */
  async recordPayment(id: string, request: RecordPaymentRequest): Promise<ServiceInvoicePayment> {
    const { data } = await api.post<ServiceInvoicePayment>(
      `/billing/invoices/${id}/payment`,
      request,
    );
    return data;
  },

  /**
   * Void invoice
   * POST /billing/invoices/{id}/void
   */
  async voidInvoice(id: string): Promise<ServiceInvoice> {
    const { data } = await api.post<ServiceInvoice>(`/billing/invoices/${id}/void`, {});
    return data;
  },

  /**
   * Send invoice by email to client
   * POST /billing/invoices/{id}/send
   */
  async sendInvoice(id: string): Promise<ServiceInvoice> {
    const { data } = await api.post<ServiceInvoice>(`/billing/invoices/${id}/send`, {});
    return data;
  },

  /**
   * Link invoice to Stripe invoice
   * POST /billing/invoices/{id}/link
   */
  async linkInvoice(id: string, request: LinkInvoiceRequest): Promise<ServiceInvoice> {
    const { data } = await api.post<ServiceInvoice>(`/billing/invoices/${id}/link`, request);
    return data;
  },

  // ─── Public Quote (no auth) ───────────────────────────────

  /**
   * Get public quote by token (no authentication)
   * GET /public/billing/quote/{token}
   */
  async getPublicQuote(token: string): Promise<ServiceQuote> {
    const { data } = await api.get<ServiceQuote>(`/public/billing/quote/${token}`);
    return data;
  },

  /**
   * Accept public quote by token
   * POST /public/billing/quote/{token}/accept
   */
  async acceptPublicQuote(token: string): Promise<ServiceQuote> {
    const { data } = await api.post<ServiceQuote>(
      `/public/billing/quote/${token}/accept`,
      {},
    );
    return data;
  },

  /**
   * Reject public quote by token
   * POST /public/billing/quote/{token}/reject
   */
  async rejectPublicQuote(token: string, reason?: string): Promise<ServiceQuote> {
    const body: RejectQuoteRequest = {};
    if (reason) body.rejection_reason = reason;
    const { data } = await api.post<ServiceQuote>(
      `/public/billing/quote/${token}/reject`,
      body,
    );
    return data;
  },
};
